import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { opsApi } from '@/api/ops';
import type { SetupCheckServiceView, SetupCheckView } from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { StatusChip } from '@/components/ops/Badges';
import { Section } from '@/components/ops/Layout';
import { MutationError } from '@/components/ops/Problem';
import { formatTimestamp } from '@/ops/format';
import { stepName } from '@/ops/stepNames';

type Tone = 'success' | 'warning' | 'danger' | 'muted';

/** One service's verdict in words, with what to do about it. */
export function setupVerdict(
  s: Pick<SetupCheckServiceView, 'status' | 'failing_checks'>,
  /** Who can act: an owner on a tenant the console set up retries the step; anyone else is told who can. */
  fix: 'retry' | 'ask_owner' | 'outside_console' = 'retry',
): { label: string; tone: Tone; hint?: string } {
  const retry = fix === 'retry'
    ? "Retry the matching step on the tenant's setup job."
    : fix === 'ask_owner'
      ? "An owner can retry the matching step on the tenant's setup job."
      : 'This tenant was created outside the console — fix it with the deploy tools.';
  switch (s.status) {
    case 'ok':
      return { label: 'Set up correctly', tone: 'success' };
    case 'red':
      return {
        label: 'Something is missing',
        tone: 'danger',
        hint: `${s.failing_checks.length ? `Failing: ${s.failing_checks.join(', ')}. ` : ''}${retry}`,
      };
    case 'not_set_up':
      return { label: 'Not set up for this tenant', tone: 'warning', hint: `The service knows nothing about the tenant. ${retry}` };
    case 'unavailable':
      return { label: 'Could not ask', tone: 'warning', hint: 'The service is down or too slow right now — check again later.' };
    case 'not_configured':
      return { label: 'Not connected here', tone: 'muted', hint: 'The console has no address or key for this service on this environment.' };
    case 'endpoint_absent':
      return { label: 'Cannot check itself yet', tone: 'muted', hint: 'This service version predates its check.' };
    default:
      return { label: 'The check failed', tone: 'danger', hint: 'The service refused or gave an unreadable answer.' };
  }
}

/** "Check chat (SalesIQ)" → "Chat (SalesIQ)". */
const serviceName = (key: string) => {
  const name = stepName(key).replace(/^Check /, '');
  return name.charAt(0).toUpperCase() + name.slice(1);
};

/**
 * The tenant's setup check (2026-09-30): the setup job's final checks, re-run on demand — live and read-only, one
 * verdict per service. Run by a button, never on page load: it asks every service of the environment.
 */
export function SetupCheckCard({ env, tenantId, jobId }: { env: string; tenantId: string; jobId: string | null }) {
  const check = useMutation({ mutationFn: () => opsApi.tenants.setupCheck(env, tenantId) });
  const view: SetupCheckView | undefined = check.data;
  const needLook = view ? view.services.filter((s) => s.status !== 'ok').length : 0;
  const canOpenJobs = hasPermission(PERMISSIONS.tenantProvision);

  return (
    <Section
      title="Setup check"
      meta={view ? `checked ${formatTimestamp(view.checked_at)}` : 'asks every service whether this tenant is set up'}
      actions={
        <button type="button" className="btn btn-ghost btn-sm" disabled={check.isPending} onClick={() => check.mutate()}>
          {check.isPending ? 'Checking…' : view ? 'Check again' : 'Check now'}
        </button>
      }
    >
      <MutationError error={check.error} />
      {!view && !check.isError && (
        <p className="t-body muted" style={{ margin: 0 }}>
          Runs the same final checks as the setup job, without changing anything.
        </p>
      )}
      {view && (
        <div className="stack-sm">
          <p className="t-body" style={{ margin: 0 }} data-testid="setup-summary">
            {view.services.length === 0
              ? 'No checks are configured for this environment.'
              : view.all_ok
                ? `Everything is set up — ${view.services.length} services checked.`
                : `${needLook} of ${view.services.length} services need a look.`}
            {!view.all_ok && jobId && canOpenJobs && <> <Link to={`/ops/provisioning/jobs/${jobId}`}>Open the setup job</Link></>}
          </p>
          <ul className="attention-list">
            {view.services.map((s) => {
              const verdict = setupVerdict(s, !jobId ? 'outside_console' : canOpenJobs ? 'retry' : 'ask_owner');
              return (
                <li key={s.key} className="attention-item">
                  <div className="attention-text">
                    <span className="t-body">{serviceName(s.key)}</span>
                    {verdict.hint && <span className="t-caption muted">{verdict.hint}</span>}
                  </div>
                  <StatusChip status={verdict.label} tone={verdict.tone} title={s.error_code ?? undefined} />
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Section>
  );
}
